import React from "react";

import {
    Dialog,
    DialogContent,
    DialogContentText,
    Box,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    Autocomplete,
    TextField,
    Grid,
} from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useUtilityContext } from "../../../utility-provider";
import { setAddSurveyObj } from "../../../features/locationAddSurveySlice";
import { setApprovalStatus } from "../../../features/approvalSurveySlice";
import { apiRequest, postPython } from "../../../api/ApiCall";
import MessageDialog from "../../components/MessageDialog";
import { LOCAL_CONSTANT } from "../../Const";
import { userLogging, readFirestoreDoc } from "../../../utility/firestore";
import { encryptQueryParam } from "../../../utility/util";
import { useUserContext } from "../../../user-provider";
import { getStaffNoList } from "../../mailFunction";
import { mailDetail } from "../../mailTemplate";
import { settingTaskId } from "../../components/LocationAddSurveyFunc";

export interface approvalSurveyModalProps {
    props: {
        open: boolean;
        handleModal: any;
        planAreaId: string | null;
        branch: string | null;
        userData: any;
        receptionBook: any;
        planAreaInfoData: any;
        locationBase: any;
    };
}

const ApprovalSurveyModal: React.FC<approvalSurveyModalProps> = ({ props }) => {
    const dispatch = useAppDispatch();
    const utilityCtx = useUtilityContext();
    const user = useUserContext();

    const [open, setOpen] = React.useState(false);

    const objLocationAddSurvey = useAppSelector(
        (state) => state.locationAddSurvey.obj
    );
    const cover = useAppSelector((state) => state.locationAddSurvey.objcover);
    const objApprovalSurvey = useAppSelector(
        (state) => state.approvalSurvey.approvalStatus
    );

    const [message, setMessage] = React.useState<string>("");
    const [messageDialogOpen, setMessageDialogOpen] =
        React.useState<boolean>(false);
    const [hasButton, setHasButton] = React.useState<boolean>(false);

    // 代理承認関連のstate
    const [showProxyApprovalCheckbox, setShowProxyApprovalCheckbox] = React.useState<boolean>(false);
    const [proxyApprovalChecked, setProxyApprovalChecked] = React.useState<boolean>(false);
    const [proxyApprovalUser, setProxyApprovalUser] = React.useState<string>("");
    const [proxyApproverList, setProxyApproverList] = React.useState<any[]>([]);

    React.useEffect(() => {
        if (props.open) {
            setOpen(props.open);
        }
    }, [props.open]);

    // 代理承認チェックボックス表示条件をチェック（RMの場合、タスクIDがE002の時のみ表示）
    React.useEffect(() => {
        const checkProxyApprovalConditions = async () => {
            try {
                // タスクIDがE002かチェック
                if (props.receptionBook?.task_id !== "E002") {
                    setShowProxyApprovalCheckbox(false);
                    return;
                }

                // ログインユーザーがRMかチェック（job_category_code_oa = '230'）
                const userData = JSON.parse(user.userData);
                const isRM = userData.job_category_code_OA === "230";
                if (!isRM) {
                    setShowProxyApprovalCheckbox(false);
                    return;
                }

                // RMの場合、プルダウンリストを作成
                setShowProxyApprovalCheckbox(true);

                const staffMasterStr = localStorage.getItem("staffMaster");

                if (staffMasterStr) {
                    const staffMaster = JSON.parse(staffMasterStr);

                    // Firestoreからlocation_proxy_approval部署リストを取得
                    const departmentMap = await readFirestoreDoc("code_master", "location_proxy_approval", "department");

                    if (departmentMap) {
                        // Firestoreに登録されている全てのtop_department_code_oaを取得
                        const allowedTopDepartments = Object.keys(departmentMap);

                        // job_category_code_oa = '101' (GM) かつ allowedTopDepartmentsに含まれるtop_department_code_oaを持つ職員
                        const filteredStaff = staffMaster
                            .filter((staff: any) =>
                                staff.job_category_code_oa === "101" &&
                                allowedTopDepartments.includes(staff.department_code_oa)
                            );

                        const sortedStaff = filteredStaff.sort((a: any, b: any) => {
                            if (a.department_code_oa < b.department_code_oa) return -1;
                            if (a.department_code_oa > b.department_code_oa) return 1;
                            return 0;
                        });

                        // プルダウンリスト用に整形
                        const approverList: any[] = [];
                        sortedStaff.forEach((staff: any) => {
                            if (staff.staff_name) {
                                approverList.push({
                                    label: staff.staff_name,
                                    staff_number: staff.staff_number,
                                    department_code_oa: staff.department_code_oa,
                                    department_name_oa: staff.department_name_oa,
                                    job_category_code_oa: staff.job_category_code_oa,
                                });
                            }
                        });
                        setProxyApproverList(approverList);

                        //proxy_approval_user_gm に値が入っている場合、初期値として設定
                        if (props.locationBase?.proxy_approval_user_gm) {
                            const initialApprover = approverList.find(
                                (approver) => approver.staff_number === props.locationBase.proxy_approval_user_gm
                            );
                            if (initialApprover) {
                                setProxyApprovalUser(initialApprover.label);
                                setProxyApprovalChecked(true);
                            }
                        }
                    } else {
                        // departmentMapが取得できない場合はリストを空にする
                        setProxyApproverList([]);
                    }
                }
            } catch (error) {
                console.error("Error checking proxy approval conditions:", error);
                setShowProxyApprovalCheckbox(false);
            }
        };

        if (user.userData && props.receptionBook?.task_id && props.open) {
            checkProxyApprovalConditions();
        }
    }, [user.userData, props.receptionBook?.task_id, props.locationBase?.proxy_approval_user_gm, props.open]);

    const handleClose = () => {
        dispatch(
            setApprovalStatus({
                ...objApprovalSurvey,
                additionalResearch: "",
            })
        );
        setOpen(false);
        props.handleModal();
    };

    const handleRadioChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        labelName: string
    ) => {
        dispatch(
            setApprovalStatus({
                ...objApprovalSurvey,
                [labelName]: e.target.value,
            })
        );
    };

    const handleDisabled = () => {
        let disabled = false;
        if (objApprovalSurvey.additionalResearch === "") {
            disabled = true;
        }

        return disabled;
    };

    const onRegist = () => {
        (async () => {
            //差戻フラグ(差戻 = true、承認 = false)
            const sendBackFlag: boolean = objApprovalSurvey.additionalResearch === "0";
            try {
                if (utilityCtx.showSpinner) {
                    utilityCtx.showSpinner();
                }
                // メッセージモーダル表示
                setHasButton(false);
                if (sendBackFlag) {
                    setMessage("差戻中です");
                } else {
                    setMessage("承認中です");
                }
                setMessageDialogOpen(true);
                const managerName = props.userData.user_name;
                let department = "";
                const staffMasterObj = localStorage.getItem("staffMaster");
                if (staffMasterObj !== null) {
                    const jsonStaffMasterObj = JSON.parse(staffMasterObj);
                    jsonStaffMasterObj.forEach((row: any) => {
                        if (
                            row.department_code_oa ===
                            props.userData.department_code_OA
                        ) {
                            department = row.department_name_oa;
                        }
                    });
                }

                // 選択された代理承認者のstaff_numberを取得
                const selectedApprover = proxyApproverList.find(
                    (e) => e.label === proxyApprovalUser
                );
                const proxyApprovalUserGm = proxyApprovalChecked && selectedApprover
                    ? selectedApprover.staff_number
                    : "";

                // 印影の作成
                // タスクIDに基づいてproxy_approval_flagを設定
                let proxyApprovalFlag = false;
                const taskId = props.receptionBook?.task_id;
                if (["E001", "E002"].includes(taskId)) {
                    // RM承認の場合（代理承認ユーザーがログインユーザーのstaff_numberと同じならtrue）
                    proxyApprovalFlag =
                        !!props.locationBase?.proxy_approval_user_rm &&
                        props.locationBase.proxy_approval_user_rm ===
                            props.userData?.staff_number;
                } else if (["E003"].includes(taskId)) {
                    // GM承認の場合（代理承認ユーザーがログインユーザーのstaff_numberと同じならtrue）
                    proxyApprovalFlag =
                        !!props.locationBase?.proxy_approval_user_gm &&
                        props.locationBase.proxy_approval_user_gm ===
                            props.userData?.staff_number;
                }


                const paythonParams = {
                    mode: "regist",
                    endPoint: "/make-iner",
                    query: {
                        prop_site_no: props.planAreaId,
                        branch: props.branch,
                        managerName: managerName,
                        department: department,
                        proxy_approval_flag: proxyApprovalFlag,
                    },
                };
                const result = await postPython(paythonParams);
                let paramsData: any = {};
                if (result.data.data.inei_file_path) {
                    paramsData = await approvalMain({
                        planAreaId: props.planAreaId,
                        branch: props.branch,
                        inei_file_path: result.data.data.inei_file_path,
                        target: "parent",
                        target_plan_area_id: props.planAreaId,
                        planAreaInfoData: props.planAreaInfoData,
                        userData: props.userData,
                        sendBackFlag: sendBackFlag,
                        proxyApprovalFlag: proxyApprovalFlag,
                        proxy_approval_user_gm: proxyApprovalUserGm,
                    });
                }
                const params = {
                    mode: "regist",
                    endPoint: "/excel-add-print-pdf",
                    query: {
                        plan_area_id: props.planAreaId,
                        branch: props.branch,
                        file_name: objLocationAddSurvey.cover_confirm_pdf_path
                            .toString()
                            .replace("/pdf/", ""),
                        task_id: paramsData.taskId,
                        mode: "approval",
                    },
                };
                userLogging(
                    LOCAL_CONSTANT.FUNCTION_NAME.APPROVAL_SURVEY_PDF,
                    params,
                    user
                );
                // PDF変換
                if (
                    ["E003", "E008", "E015", "E018"].includes(paramsData.taskId)
                ) {
                    await postPython(params);
                }
                let addSurvey = JSON.parse(
                    JSON.stringify(paramsData.addSurvey)
                );
                addSurvey.doc_pdf_path = JSON.parse(addSurvey.doc_pdf_path);
                dispatch(setAddSurveyObj(addSurvey));
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
                // 依頼コメント画面と同様に、承認後はモーダルを閉じないよう閉じるボタンを非表示
                setHasButton(false);
                if (sendBackFlag) {
                    setMessage("差し戻しました");
                } else {
                    setMessage("承認しました");
                }
            } catch (e) {
                console.log(e);
                setHasButton(true);
                if (sendBackFlag) {
                    setMessage("差戻に失敗しました");
                } else {
                    setMessage("承認に失敗しました");
                }
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            }
        })();
    };

    const approvalMain = async (param: any) => {
        const paramsData = settingTaskId(param, {
            objApprovalSurvey: objApprovalSurvey,
            objLocationAddSurvey: objLocationAddSurvey,
            userData: props.userData,
            task_id: props.receptionBook?.task_id,
        });

        let titleDetail = "";
        let staffNoList: any = [];
        let detail: any = {};
        const urlQueryParam = `planAreaId=${props.planAreaId}&branch=${props.branch}`;
        const encryptedQueryParam = await encryptQueryParam(urlQueryParam);

        if (paramsData.hasMail) {
            let mailDetailTarget = "requestCommentAddSurvey";
            let staffNoListTarget = "addSurvey";
            if (["E003"].includes(paramsData.taskId)) {
                mailDetailTarget = "addSurvey";
            }
            if (["E015"].includes(paramsData.taskId)) {
                mailDetailTarget = "requestCommentAddSurveyConfirmed";
                staffNoListTarget = "addSurveyRR";
            }
            staffNoList = getStaffNoList(staffNoListTarget, {
                staffMasterObj: localStorage.getItem("staffMaster"),
                planAreaInfoData: param.planAreaInfoData,
            });
            if (["E003"].includes(paramsData.taskId) && param.proxy_approval_user_gm) {
                staffNoList.push(param.proxy_approval_user_gm);
                mailDetailTarget = "addSurveyProxyApproval";
            }
            const mailDetailParam = {
                survey_name: cover.survey_name,
                titleDetail: titleDetail,
                url: `${process.env.REACT_APP_HOST}/approval-survey?${encryptedQueryParam}`,
                rfc: cover.survey_creator,
                type: "01",
                staffNoList: staffNoList,
                plan_area_id: props.planAreaId,
                branch: props.branch,
                notficationType: "01",
            };  
            detail = mailDetail(mailDetailTarget, mailDetailParam);
        }
        const params = {
            mode: "regist",
            endPoint: "/location/Reception/v1/approvalAddSurvey",
            query: {
                plan_area_id: param.planAreaId,
                branch: param.branch,
                task_id: paramsData.taskId,
                addSurveyData: paramsData.addSurvey,
                detail: detail,
                userData: param.userData,
                sendBackFlag: param.sendBackFlag,
                proxyApprovalFlag: param.proxyApprovalFlag,
                proxy_approval_user_gm: param.proxy_approval_user_gm,
            },
        };
        await apiRequest(params);

        return paramsData;
    };

    const closeMessageDialog = () => {
        handleClose();
        setMessage("");
        setHasButton(false);
        setMessageDialogOpen(false);
    };

    return (
        <div>
            <Dialog
                open={open}
                keepMounted
                aria-describedby="alert-dialog-slide-description"
                sx={{ display: messageDialogOpen ? "none" : "block" }}
            >
                <DialogContent>
                    <Box>
                        <DialogContentText
                            id="alert-dialog-slide-description"
                            sx={{ whiteSpace: "pre-wrap" }}
                        >
                            「追加調査」を承認しますか
                        </DialogContentText>
                        <RadioGroup
                            row
                            onChange={(e) =>
                                handleRadioChange(e, "additionalResearch")
                            }
                            value={objApprovalSurvey.additionalResearch}
                        >
                            <FormControlLabel
                                value={"1"}
                                control={<Radio />}
                                label={"承認"}
                            />
                            <FormControlLabel
                                value={"0"}
                                control={<Radio />}
                                label={"差戻"}
                            />
                        </RadioGroup>
                    </Box>
                    {["E010", "E012"].includes(
                        props.receptionBook?.task_id
                    ) && (
                        <Box>
                            <DialogContentText
                                id="alert-dialog-slide-description"
                                sx={{ whiteSpace: "pre-wrap" }}
                            >
                                RR GM確認が必要ですか
                            </DialogContentText>
                            <RadioGroup
                                row
                                onChange={(e) =>
                                    handleRadioChange(
                                        e,
                                        "confirmAdditionalResearch"
                                    )
                                }
                                value={
                                    objApprovalSurvey.confirmAdditionalResearch
                                }
                            >
                                <FormControlLabel
                                    value={"1"}
                                    control={<Radio />}
                                    label={"必要"}
                                />
                                <FormControlLabel
                                    value={"0"}
                                    control={<Radio />}
                                    label={"不要"}
                                />
                            </RadioGroup>
                        </Box>
                    )}

                    {/* 代理承認のチェックボックスとプルダウン */}
                    {showProxyApprovalCheckbox && (
                        <Box
                            sx={{
                                marginTop: "24px",
                                marginBottom: "16px",
                                padding: "16px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={proxyApprovalChecked}
                                                onChange={(e) => {
                                                    setProxyApprovalChecked(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setProxyApprovalUser("");
                                                    }
                                                }}
                                            />
                                        }
                                        label="代理承認を依頼しますか"
                                        sx={{ marginBottom: "8px" }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        size="small"
                                        options={proxyApproverList}
                                        value={proxyApproverList.find((e) => e.label === proxyApprovalUser) || null}
                                        onChange={(e, val) => setProxyApprovalUser(val?.label ?? "")}
                                        getOptionLabel={(option) => {
                                            if (!option.label) return "";
                                            return `${option.label}（${option.department_name_oa || ""}）`;
                                        }}
                                        isOptionEqualToValue={(option, v) => option.label === v.label}
                                        disabled={!proxyApprovalChecked}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="代理承認者"
                                                variant="outlined"
                                                placeholder={proxyApprovalChecked ? "選択してください" : ""}
                                            />
                                        )}
                                        sx={{ width: "100%" }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Box
                        sx={{ display: "flex", justifyContent: "space-around" }}
                    >
                        <Box sx={{ textAlign: "center", margin: "10px" }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleClose}
                            >
                                キャンセル
                            </Button>
                        </Box>
                        <Box sx={{ textAlign: "center", margin: "10px" }}>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={handleDisabled()}
                                onClick={onRegist}
                            >
                                送信
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
            <MessageDialog
                props={{
                    open: messageDialogOpen,
                    handleModal: closeMessageDialog,
                    body: message,
                    hasButton: hasButton,
                    buttonBody: LOCAL_CONSTANT.LABEL.CLOSE,
                }}
            />
        </div>
    );
};

export default ApprovalSurveyModal;
